import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Radio } from '../../../../design/inputs/radio/S_Radio';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { ProjectPublishStrategy } from '../projectDetails.models';

interface IPublishDatasetConfirmationModalProps {
  showModal: boolean;
  draftsCount: number;
  hasImportedAnnotations: boolean;
  strategy: ProjectPublishStrategy;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  onStrategyChange: (strategy: ProjectPublishStrategy) => void;
  projectId?: string;
  disablePublishButton?: boolean;
}

export const ProjectPublishConfirmationModal = observer((props: IPublishDatasetConfirmationModalProps): ReactElement => {
  const { t } = useTranslator();

  const onStrategyChange = (data: ProjectPublishStrategy) => (): void => {
    props.onStrategyChange(data);
  };

  const loaderKey = 'publish-button-loader';

  return (
    <S_Modal
      isOpen={props.showModal}
      toggle={props.onCancel}
      headerText={t('confirm_project_publish')}
      buttons={
        <>
          <ButtonWithLoader
            color='neutral'
            variant='outlined'
            id='project-cancel-button'
            onClick={props.onCancel}
            disabled={props.disablePublishButton}
          >
            {t('cancel')}
          </ButtonWithLoader>
          <ButtonWithLoader
            onClick={props.onConfirm}
            loaderKey={props.projectId ? `${loaderKey}-${props.projectId}` : loaderKey}
            disabled={props.disablePublishButton}
          >
            {t('publish')}
          </ButtonWithLoader>
        </>
      }
    >
      <p>{t('publishing_the_project_will_limit_editing')}</p>
      {props.draftsCount > 0 && (
        <>
          <p>
            {
              <Trans
                i18nKey={'project_publish_drafts_warning'}
                count={props.draftsCount}
                values={{ draftsCount: props.draftsCount.toString(), count: props.draftsCount }}
                components={{ b: <b /> }}
              />
            }
            {props.hasImportedAnnotations && <span> {t('imported_annotations_remain_unchanged')}</span>}
          </p>
          <ColumnLayout size='sm'>
            <S_Radio
              id='option-review'
              onChange={onStrategyChange(ProjectPublishStrategy.DiscardDrafts)}
              checked={props.strategy === ProjectPublishStrategy.DiscardDrafts}
              label={t('publish_project_strategy_discard')}
            />
            <S_Radio
              id='option-leave'
              onChange={onStrategyChange(ProjectPublishStrategy.KeepDrafts)}
              checked={props.strategy === ProjectPublishStrategy.KeepDrafts}
              label={t('publish_project_strategy_keep')}
            />
          </ColumnLayout>
        </>
      )}
    </S_Modal>
  );
});
